export interface BackupColumn {
  name: string;
  kind: 'text' | 'int' | 'bool' | 'timestamptz' | 'date' | 'uuid' | 'enum';
  enumName?: string;
}

export interface BackupTable {
  name: string;
  columns: readonly BackupColumn[];
  primaryKey: string;
  overrideIdentity: boolean;
  orderColumn: string;
}

const text = (name: string): BackupColumn => ({ name, kind: 'text' });
const integer = (name: string): BackupColumn => ({ name, kind: 'int' });
const bool = (name: string): BackupColumn => ({ name, kind: 'bool' });
const timestamp = (name: string): BackupColumn => ({ name, kind: 'timestamptz' });
const date = (name: string): BackupColumn => ({ name, kind: 'date' });

export const STUDENT_IMPORT_BACKUP_TABLES: readonly BackupTable[] = [
  {
    name: 'workout_assignments',
    primaryKey: 'id',
    overrideIdentity: false,
    orderColumn: 'id',
    columns: [
      integer('id'),
      integer('student_id'),
      { name: 'status', kind: 'enum', enumName: 'workout_assignment_status' },
      timestamp('delivered_at'),
      text('notes'),
      timestamp('created_at'),
      timestamp('updated_at'),
      date('previous_competence'),
      date('expected_delivery_date'),
      integer('delivered_by_portal_user_id'),
      text('current_training_code'),
      text('next_training_code'),
    ],
  },
  {
    name: 'student_workout_history',
    primaryKey: 'id',
    overrideIdentity: true,
    orderColumn: 'id',
    columns: [
      integer('id'),
      integer('student_id'),
      text('training_period'),
      text('workout_code'),
      timestamp('delivered_at'),
      integer('delivered_by_portal_user_id'),
      text('last_workout_date'),
      integer('workout_assignment_id'),
      timestamp('created_at'),
    ],
  },
  {
    name: 'students_fup',
    primaryKey: 'id',
    overrideIdentity: true,
    orderColumn: 'id',
    columns: [
      integer('id'),
      integer('student_id'),
      text('workout_code'),
      text('fup_summary'),
      text('main_questions'),
      timestamp('created_at'),
      timestamp('updated_at'),
      text('status'),
      text('response'),
      integer('workout_assignment_id'),
      text('last_cycle_feedback'),
      text('next_cycle_adjustments'),
      { name: 'weekly_availability', kind: 'enum', enumName: 'weekly_availability' },
      { name: 'available_training_time', kind: 'enum', enumName: 'available_training_time_enum' },
      text('additional_notes'),
    ],
  },
  {
    name: 'student_fup_links',
    primaryKey: 'token',
    overrideIdentity: false,
    orderColumn: 'created_at',
    columns: [
      text('token'),
      integer('workout_assignment_id'),
      timestamp('created_at'),
      integer('attempt_number'),
      timestamp('responded_at'),
      bool('inactive'),
      timestamp('expires_at'),
    ],
  },
  {
    name: 'student_notes',
    primaryKey: 'id',
    overrideIdentity: true,
    orderColumn: 'id',
    columns: [
      integer('id'),
      integer('student_id'),
      integer('portal_user_id'),
      text('note'),
      timestamp('created_at'),
      timestamp('deleted_at'),
    ],
  },
  {
    name: 'student_note_summaries',
    primaryKey: 'student_id',
    overrideIdentity: false,
    orderColumn: 'student_id',
    columns: [
      integer('student_id'),
      text('summary'),
      timestamp('updated_at'),
    ],
  },
];

const quote = (value: string): string => `'${value.replace(/'/g, "''")}'`;

const sqlValue = (value: unknown, column: BackupColumn): string => {
  if (value === null || value === undefined) {
    return 'NULL';
  }

  if (column.kind === 'bool') {
    return value === true ? 'true' : 'false';
  }

  if (column.kind === 'int') {
    return String(value);
  }

  if (column.kind === 'date') {
    return quote(String(value).slice(0, 10));
  }

  if (column.kind === 'enum') {
    return `${quote(String(value))}::public.${column.enumName}`;
  }

  return quote(String(value));
};

const insertStatement = (table: BackupTable, row: Record<string, unknown>): string => {
  const columns = table.columns.map((column) => column.name).join(', ');
  const values = table.columns.map((column) => sqlValue(row[column.name], column)).join(', ');
  const override = table.overrideIdentity ? ' OVERRIDING SYSTEM VALUE' : '';
  return `INSERT INTO public.${table.name} (${columns})${override} VALUES (${values}) ON CONFLICT (${table.primaryKey}) DO NOTHING;`;
};

const sequenceReset = (table: BackupTable): string | null => {
  if (table.primaryKey !== 'id') {
    return null;
  }
  if (table.name === 'workout_assignments') {
    return "SELECT setval('public.workout_assignments_id_seq', GREATEST((SELECT COALESCE(MAX(id), 1) FROM public.workout_assignments), 1));";
  }
  return `SELECT setval(pg_get_serial_sequence('public.${table.name}', 'id'), GREATEST((SELECT COALESCE(MAX(id), 1) FROM public.${table.name}), 1));`;
};

export const studentImportBackupFileName = (generatedAt: Date): string => {
  const month = String(generatedAt.getMonth() + 1).padStart(2, '0');
  const day = String(generatedAt.getDate()).padStart(2, '0');
  return `backup-importacoes-${generatedAt.getFullYear()}-${month}-${day}.sql`;
};

export const buildStudentImportBackupSql = (
  rowsByTable: Readonly<Record<string, readonly Record<string, unknown>[]>>,
  generatedAt: Date,
): string => {
  const sections = STUDENT_IMPORT_BACKUP_TABLES.map((table) => {
    const rows = rowsByTable[table.name] ?? [];
    const inserts = rows.map((row) => insertStatement(table, row));
    return [`-- ${table.name}`, ...inserts].join('\n');
  });
  const sequences = STUDENT_IMPORT_BACKUP_TABLES
    .map(sequenceReset)
    .filter((statement): statement is string => statement !== null);

  return [
    '-- Backup de FUP, histórico de treino das alunas e observações.',
    `-- Gerado em ${generatedAt.toISOString()}.`,
    '-- Linhas que já existem são mantidas.',
    'BEGIN;',
    '',
    sections.join('\n\n'),
    '',
    sequences.join('\n'),
    'COMMIT;',
    '',
  ].join('\n');
};
